import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

/**
 * TileSphere i18n resource bundles — English (fallback), Gujarati, Hindi.
 * All UI strings live under a single flat resource per language. Missing keys
 * fall back to English, then to the raw key. Use the `t()` hook everywhere:
 *   const { t } = useTranslation();
 *   <button>{t("common.save")}</button>
 */

const en = {
  common: {
    back: "Back",
    save: "Save",
    cancel: "Cancel",
    create: "Create",
    update: "Update",
    delete: "Delete",
    edit: "Edit",
    submit: "Submit",
    approve: "Approve",
    reject: "Reject",
    close: "Close",
    search: "Search",
    filter: "Filter",
    loading: "Loading…",
    yes: "Yes",
    no: "No",
    active: "Active",
    inactive: "Inactive",
    date: "Date",
    total: "Total",
    status: "Status",
    action: "Action",
    actions: "Actions",
    number: "Number",
    name: "Name",
    email: "Email",
    phone: "Phone",
    address: "Address",
    quantity: "Quantity",
    price: "Price",
    amount: "Amount",
    unit: "Unit",
    hsn: "HSN",
    notes: "Notes",
    description: "Description",
    dashboard: "Dashboard",
    overview: "Overview",
    settings: "Settings",
    logout: "Sign out",
    signIn: "Sign in",
    signOut: "Sign out",
    changePassword: "Change password",
    welcome: "Welcome",
    download: "Download",
    upload: "Upload",
    print: "Print",
    share: "Share",
    shareEmail: "Share via Email",
    shareWhatsApp: "Share via WhatsApp",
    copyLink: "Copy link",
    linkCopied: "Link copied",
    invoice: "Invoice",
    ewayBill: "E-way bill",
    challan: "Challan",
    purchaseOrder: "Purchase Order",
    grn: "GRN",
    customer: "Customer",
    vendor: "Vendor",
    product: "Product",
    getStarted: "Get started",
    register: "Register",
    explore: "Explore",
    exploreMore: "Explore more",
    learnMore: "Learn more",
    language: "Language",
    twoFactor: "Two-factor auth",
    auditLog: "Audit log",
    plan: "Plan",
    daysLeft: "{{days}}d left",
    role: "Role",
  },
  language: {
    label: "Language",
    en: "English",
    gu: "ગુજરાતી",
    hi: "हिन्दी",
  },
  nav: {
    org: {
      overview: "Overview", purchase: "Purchase", vendors: "Vendors",
      customers: "Customers", stock: "Stock & WH", qc: "Quality control",
      production: "Production", finance: "Finance", dispatch: "Dispatch",
      approvals: "Approvals inbox", users: "Users & credentials",
      roles: "Roles & permissions", companyProfile: "Company profile",
      subscription: "Subscription", auditCa: "CA Audit", security: "Security controls",
      sessions: "Active sessions", loginAudit: "Login audit", integrations: "Integrations",
      bulkUpload: "Bulk upload permissions",
    },
    admin: {
      dashboard: "Dashboard", organizations: "Organizations", subscriptions: "Subscriptions",
      vendors: "Vendors", distributors: "Distributors",
      platformUsers: "Platform users", rolesPermissions: "Roles & permissions",
      marketplace: "Marketplace", productMasters: "Product masters", payments: "Payments",
      supportCenter: "Support center", monitoring: "Monitoring", security: "Security",
      notifications: "Notifications", reports: "Reports", cms: "CMS",
      integrations: "Integrations", globalSettings: "Global settings",
      platformPlans: "Platform · Plans", freeTrials: "Platform · Free trials",
      platformRoles: "Platform · Role engine", platformHierarchy: "Platform · Hierarchy",
      platformPricing: "Platform · Pricing", platformLeads: "Platform · Leads",
      platformSubs: "Platform · Subscriptions", auditLogs: "Audit logs",
    },
    groups: {
      admin: "Admin · Governance",
      settings: "Settings",
      platformConsole: "Platform Console",
      organization: "Organization",
      buyer: "Buyer",
      vendor: "Vendor",
      sales: "Sales",
      dealer: "Dealer",
      distributor: "Distributor",
      warehouse: "Warehouse",
    },
  },
  landing: {
    navPlatform: "Platform",
    navHierarchy: "Hierarchy",
    navPortals: "Portals",
    heroTag: "TileSphere · the ceramic ecosystem",
    heroTitleLine1: "One ecosystem.",
    heroTitleLine2: "Every role connected.",
    heroSub: "From kiln to customer — Organization → Distributor → Vendor → Dealer → Customer. All in one configurable, multi-tenant platform.",
    platformKicker: "Platform overview",
    platformTitle1: "Every workflow your ceramic",
    platformTitle2: "business runs — in one ecosystem.",
    platformSub: "From kiln to customer, TileSphere connects every department, partner and pallet with one source of truth.",
    configurable: "Configurable · multi-tenant",
    hierarchyKicker: "Role hierarchy",
    hierarchyTitle1: "Explore every role",
    hierarchyTitle2: "in the ecosystem.",
    hierarchySub: "Click any role to expand its children. Search, filter — see exactly how your team will map onto TileSphere.",
    portalsKicker: "Choose your portal",
    portalsTitle1: "Three portals.",
    portalsTitle2: "One ecosystem.",
    portalsSub: "Each portal is purpose-built — but they speak the same data, the same brand, the same audit log.",
    explorePortal: "Explore portal",
    footerRights: "© 2026 TileSphere · An ecosystem by Projexino",
    core: "core",
    modules: {
      organization: "Organization",
      distributor: "Distributor",
      vendor: "Vendor",
      dealer: "Dealer",
      customer: "Customer",
      manufacturing: "Manufacturing",
      inventory: "Inventory",
      warehouse: "Warehouse",
      sales: "Sales",
      crm: "CRM",
      marketplace: "Marketplace",
      finance: "Finance",
      production: "Production",
      analytics: "Analytics",
      iotReady: "IoT Ready",
      cloud: "Cloud",
    },
    portals: {
      orgTitle: "Organization Portal",
      orgTagline: "Full-stack ERP for ceramic manufacturers",
      orgF1: "Manufacturing & Kiln",
      orgF2: "Sales · CRM · Marketplace",
      orgF3: "Warehouse · Dispatch",
      orgF4: "Finance · Compliance",
      distTitle: "Distributor Portal",
      distTagline: "Multi-dealer regional ops & BI",
      distF1: "Dealer onboarding",
      distF2: "Pricing & credit limits",
      distF3: "Logistics & e-way bills",
      distF4: "Regional BI dashboards",
      vendorTitle: "Vendor Portal",
      vendorTagline: "B2B marketplace + tender response",
      vendorF1: "Catalog management",
      vendorF2: "RFQs & tenders",
      vendorF3: "Order fulfillment",
      vendorF4: "Payments & invoicing",
    },
  },
  auth: {
    signInTitle: "Sign in",
    signInSubTenant: "to the {{tenant}} portal",
    signInSubDefault: "Use your TileSphere credentials.",
    welcomeTo: "Welcome to {{tenant}}.",
    welcomeToPrefix: "Welcome to",
    ecoTag: "TileSphere · ceramic ecosystem",
    heroTitle: "Sign in.",
    heroTitleAccent: "One ecosystem.",
    heroSub: "From kiln to customer — every role, every workflow, every partner in a single multi-tenant workspace.",
    stat1Value: "10+",
    stat1Label: "Built-in roles",
    stat2Value: "100%",
    stat2Label: "Multi-tenant",
    stat3Value: "Real",
    stat3Label: "Stripe payments",
    emailLabel: "Work email",
    passwordLabel: "Password",
    emailPlaceholder: "you@company.com",
    forgotPassword: "Forgot password?",
    signInBtn: "Sign in",
    forgotPassword: "Forgot password?",
    signingIn: "Signing in…",
    backHome: "Back to home",
    signInFailed: "Sign in failed",
    welcomeBack: "Welcome back",
    twoFactorRequired: "Two-factor required",
    codeFor: "Code for",
    authCodeLabel: "Authenticator code",
    verifyAndSignIn: "Verify and sign in",
    verifying: "Verifying…",
    cancelUseAnother: "Cancel and use a different account",
    twoFactorNeeded: "Two-factor code required",
    demoTitle: "Demo · click to autofill",
    demoPasswordLabel: "Password",
  },
  dispatch: {
    title: "Dispatch",
    subtitle: "Outbound goods with automatic Invoice, E-way bill and Challan generation.",
    newDispatch: "New dispatch",
    customer: "Customer",
    vehicleNo: "Vehicle no.",
    driver: "Driver",
    salesOrder: "Sales order",
    createBtn: "Create dispatch & generate documents",
    invoicePdf: "Invoice PDF",
    ewayPdf: "E-way bill PDF",
    challanPdf: "Challan PDF",
  },
  po: {
    title: "Purchase Orders",
    newPO: "New PO",
    vendor: "Vendor",
    lines: "Line items",
    total: "Total",
    submitForApproval: "Submit for approval",
    downloadPdf: "Download PO PDF",
  },
  finance: {
    receivables: "Receivables",
    payables: "Payables",
    invoices: "Invoices",
    aging: "Aging",
    credits: "Credit notes",
    debits: "Debit notes",
    payments: "Payments",
    ledger: "Ledger",
    outstanding: "Outstanding",
    dueDate: "Due date",
  },
  qc: {
    incoming: "Incoming QA",
    manufacturing: "Manufacturing QA",
    returns: "Customer returns QA",
    newInspection: "New inspection",
    picker: "Item name / PO number / GRN number",
  },
  toasts: {
    saved: "Saved",
    created: "Created",
    updated: "Updated",
    deleted: "Deleted",
    failed: "Failed",
    submitted: "Submitted",
    approved: "Approved",
    rejected: "Rejected",
  },
  overview: {
    orgFallback: "Organization",
    welcome: "Welcome, {{name}}.",
    subtitle: "Sales, dispatch, payments, purchase — all at a glance.",
    monthSuffix: "Sales & cash",
    purchasePulse: "Purchase pulse",
    liabilities: "Liabilities",
    kpi: {
      dispatchSqm: "Dispatch · SQM",
      dispatchSqmSub: "{{count}} dispatches",
      dispatchValue: "Dispatch · Value",
      dispatchValueSub: "Invoiced this month",
      paymentsReceived: "Payments received",
      paymentsSub: "{{count}} payments",
      arOutstanding: "Outstanding · A/R",
      arSub: "Customers still to pay",
      openPos: "Open POs",
      openPosSub: "{{count}} total",
      openPoValue: "Open PO value",
      openPoValueSub: "In-flight commitments",
      pendingPrs: "Pending PRs",
      pendingPrsSub: "{{count}} total",
      grnsFiled: "GRNs filed",
      grnsSub: "Lifetime receipts",
      apOutstanding: "Outstanding · A/P",
      apSub: "Vendors awaiting payment",
      netWc: "Net working capital",
      netWcSub: "A/R − A/P",
    },
  },
  dashboards: {
    sales: {
      welcome: "Hi {{name}}",
      subtitleManager: "Pipeline health, team performance and revenue at a glance.",
      subtitleExec: "Your leads, quotations, orders and visits for today.",
    },
    dealer: {
      welcome: "Welcome, {{name}}",
      subtitle: "Your orders, payments, loyalty and offers — all in one place.",
    },
    distributor: {
      welcome: "Hi {{name}}",
      subtitle: "Regional inventory, dealer orders, logistics and collections — all in one view.",
    },
    vendor: {
      welcome: "Welcome back, {{name}}.",
      subtitle: "A snapshot of your workspace.",
    },
    admin: {
      welcome: "Platform control room",
      subtitle: "Real-time view across every organization, user, transaction and integration on TileSphere.",
    },
    warehouse: {
      welcome: "Warehouse control tower",
      subtitle: "GRN, picking, packing, dispatch and cycle counts — all in one view.",
    },
    buyer: {
      welcome: "Welcome, {{name}}",
      subtitle: "Browse the ceramic marketplace and manage your RFQs.",
    },
  },
};

const gu = {
  common: {
    back: "પાછળ", save: "સાચવો", cancel: "રદ કરો", create: "બનાવો", update: "અપડેટ",
    delete: "કાઢી નાખો", edit: "એડિટ કરો", submit: "સબમિટ", approve: "મંજૂર કરો",
    reject: "નકારો", close: "બંધ કરો", search: "શોધો", filter: "ફિલ્ટર",
    loading: "લોડ થઈ રહ્યું છે…", yes: "હા", no: "ના", active: "એક્ટિવ", inactive: "નિષ્ક્રિય",
    date: "તારીખ", total: "કુલ", status: "સ્થિતિ", action: "ક્રિયા", actions: "ક્રિયાઓ",
    number: "નંબર", name: "નામ", email: "ઈમેલ", phone: "ફોન", address: "સરનામું",
    quantity: "જથ્થો", price: "ભાવ", amount: "રકમ", unit: "એકમ", hsn: "HSN",
    notes: "નોંધો", description: "વર્ણન", dashboard: "ડેશબોર્ડ", overview: "ઓવરવ્યુ",
    settings: "સેટિંગ્સ", logout: "સાઇન આઉટ", signIn: "સાઇન ઇન", signOut: "સાઇન આઉટ", changePassword: "પાસવર્ડ બદલો",
    welcome: "સ્વાગત છે", download: "ડાઉનલોડ", upload: "અપલોડ", print: "પ્રિન્ટ",
    share: "શેર કરો", shareEmail: "ઈમેલ દ્વારા શેર કરો", shareWhatsApp: "WhatsApp દ્વારા શેર",
    copyLink: "લિંક કૉપિ કરો", linkCopied: "લિંક કૉપિ થઈ",
    invoice: "ઇન્વોઇસ", ewayBill: "ઈ-વે બિલ", challan: "ચલણ",
    purchaseOrder: "પર્ચેઝ ઓર્ડર", grn: "GRN", customer: "ગ્રાહક", vendor: "વિક્રેતા", product: "ઉત્પાદન",
    getStarted: "શરૂ કરો", register: "રજિસ્ટર કરો", explore: "એક્સપ્લોર", exploreMore: "વધુ જુઓ", learnMore: "વધુ જાણો",
    language: "ભાષા", twoFactor: "ટૂ-ફેક્ટર ઓથ", auditLog: "ઓડિટ લોગ", plan: "પ્લાન",
    daysLeft: "{{days}} દિવસ બાકી", role: "ભૂમિકા",
  },
  language: { label: "ભાષા", en: "English", gu: "ગુજરાતી", hi: "हिन्दी" },
  nav: {
    org: {
      overview: "ઓવરવ્યુ", purchase: "ખરીદી", vendors: "વિક્રેતાઓ", customers: "ગ્રાહકો",
      stock: "સ્ટોક અને વેરહાઉસ", qc: "ગુણવત્તા નિયંત્રણ", production: "ઉત્પાદન",
      finance: "નાણાં", dispatch: "ડિસ્પેચ", approvals: "મંજૂરી ઇનબોક્સ",
      users: "વપરાશકર્તા અને લોગિન", roles: "ભૂમિકાઓ અને પરવાનગીઓ",
      companyProfile: "કંપની પ્રોફાઇલ", subscription: "સબ્સ્ક્રિપ્શન",
      auditCa: "CA ઓડિટ", security: "સુરક્ષા નિયંત્રણો",
      sessions: "એક્ટિવ સેશન્સ", loginAudit: "લોગિન ઓડિટ", integrations: "એકીકરણ",
      bulkUpload: "બલ્ક અપલોડ પરવાનગીઓ",
    },
    admin: {
      dashboard: "ડેશબોર્ડ", organizations: "સંસ્થાઓ", subscriptions: "સબ્સ્ક્રિપ્શન",
      vendors: "વિક્રેતાઓ", distributors: "વિતરકો",
      platformUsers: "પ્લેટફોર્મ વપરાશકર્તા", rolesPermissions: "ભૂમિકાઓ અને પરવાનગીઓ",
      marketplace: "માર્કેટપ્લેસ", productMasters: "પ્રોડક્ટ માસ્ટર", payments: "ચૂકવણી",
      supportCenter: "સપોર્ટ સેન્ટર", monitoring: "મોનિટરિંગ", security: "સુરક્ષા",
      notifications: "સૂચનાઓ", reports: "રિપોર્ટ્સ", cms: "CMS", integrations: "એકીકરણ",
      globalSettings: "ગ્લોબલ સેટિંગ્સ", platformPlans: "પ્લેટફોર્મ · પ્લાન",
      freeTrials: "પ્લેટફોર્મ · ફ્રી ટ્રાયલ", platformRoles: "પ્લેટફોર્મ · ભૂમિકા એન્જિન",
      platformHierarchy: "પ્લેટફોર્મ · શ્રેણી", platformPricing: "પ્લેટફોર્મ · ભાવ",
      platformLeads: "પ્લેટફોર્મ · લીડ્સ", platformSubs: "પ્લેટફોર્મ · સબ્સ્ક્રિપ્શન",
      auditLogs: "ઓડિટ લોગ",
    },
    groups: {
      admin: "એડમિન · શાસન", settings: "સેટિંગ્સ", platformConsole: "પ્લેટફોર્મ કન્સોલ",
      organization: "સંસ્થા", buyer: "ખરીદદાર", vendor: "વિક્રેતા", sales: "વેચાણ",
      dealer: "ડીલર", distributor: "વિતરક", warehouse: "વેરહાઉસ",
    },
  },
  landing: {
    navPlatform: "પ્લેટફોર્મ", navHierarchy: "શ્રેણી", navPortals: "પોર્ટલ્સ",
    heroTag: "TileSphere · સિરેમિક ઇકોસિસ્ટમ",
    heroTitleLine1: "એક જ ઇકોસિસ્ટમ.",
    heroTitleLine2: "દરેક ભૂમિકા જોડાયેલી.",
    heroSub: "કિલ્નથી ગ્રાહક સુધી — સંસ્થા → વિતરક → વિક્રેતા → ડીલર → ગ્રાહક. બધા એક જ કન્ફિગરેબલ, મલ્ટી-ટેનન્ટ પ્લેટફોર્મ પર.",
    platformKicker: "પ્લેટફોર્મ ઓવરવ્યુ",
    platformTitle1: "તમારો સિરેમિક બિઝનેસ ચલાવે તેવો દરેક વર્કફ્લો",
    platformTitle2: "— એક જ ઇકોસિસ્ટમમાં.",
    platformSub: "કિલ્નથી ગ્રાહક સુધી, TileSphere દરેક વિભાગ, ભાગીદાર અને પેલેટને એક જ સત્યના સ્ત્રોત સાથે જોડે છે.",
    configurable: "કન્ફિગરેબલ · મલ્ટી-ટેનન્ટ",
    hierarchyKicker: "ભૂમિકા શ્રેણી",
    hierarchyTitle1: "ઇકોસિસ્ટમની દરેક",
    hierarchyTitle2: "ભૂમિકા જુઓ.",
    hierarchySub: "કોઈપણ ભૂમિકા પર ક્લિક કરીને તેની ઉપશ્રેણી ખોલો. શોધો, ફિલ્ટર કરો — તમારી ટીમ TileSphere પર કેવી રીતે મેપ થશે તે જુઓ.",
    portalsKicker: "તમારો પોર્ટલ પસંદ કરો",
    portalsTitle1: "ત્રણ પોર્ટલ.",
    portalsTitle2: "એક ઇકોસિસ્ટમ.",
    portalsSub: "દરેક પોર્ટલ ખાસ હેતુ માટે બન્યો છે — પણ તેઓ સમાન ડેટા, સમાન બ્રાન્ડ અને સમાન ઓડિટ લોગ સાથે વાત કરે છે.",
    explorePortal: "પોર્ટલ એક્સપ્લોર કરો",
    footerRights: "© 2026 TileSphere · Projexino દ્વારા બનાવેલ ઇકોસિસ્ટમ",
    core: "કોર",
    modules: {
      organization: "સંસ્થા", distributor: "વિતરક", vendor: "વિક્રેતા", dealer: "ડીલર",
      customer: "ગ્રાહક", manufacturing: "ઉત્પાદન", inventory: "ઇન્વેન્ટરી",
      warehouse: "વેરહાઉસ", sales: "વેચાણ", crm: "CRM", marketplace: "માર્કેટપ્લેસ",
      finance: "નાણાં", production: "પ્રોડક્શન", analytics: "એનાલિટિક્સ",
      iotReady: "IoT તૈયાર", cloud: "ક્લાઉડ",
    },
    portals: {
      orgTitle: "સંસ્થા પોર્ટલ",
      orgTagline: "સિરેમિક ઉત્પાદકો માટે સંપૂર્ણ ERP",
      orgF1: "ઉત્પાદન અને કિલ્ન",
      orgF2: "વેચાણ · CRM · માર્કેટપ્લેસ",
      orgF3: "વેરહાઉસ · ડિસ્પેચ",
      orgF4: "નાણાં · અનુપાલન",
      distTitle: "વિતરક પોર્ટલ",
      distTagline: "મલ્ટી-ડીલર પ્રાદેશિક કામગીરી અને BI",
      distF1: "ડીલર ઓનબોર્ડિંગ",
      distF2: "ભાવ અને ક્રેડિટ મર્યાદા",
      distF3: "લોજિસ્ટિક્સ અને ઈ-વે બિલ",
      distF4: "પ્રાદેશિક BI ડેશબોર્ડ",
      vendorTitle: "વિક્રેતા પોર્ટલ",
      vendorTagline: "B2B માર્કેટપ્લેસ + ટેન્ડર જવાબ",
      vendorF1: "કેટલોગ મેનેજમેન્ટ",
      vendorF2: "RFQ અને ટેન્ડર",
      vendorF3: "ઓર્ડર પૂર્તિ",
      vendorF4: "ચૂકવણી અને બિલિંગ",
    },
  },
  auth: {
    signInTitle: "સાઇન ઇન કરો",
    signInSubTenant: "{{tenant}} પોર્ટલ પર",
    signInSubDefault: "તમારા TileSphere ક્રેડેન્શિયલ્સનો ઉપયોગ કરો.",
    welcomeTo: "{{tenant}} માં આપનું સ્વાગત છે.",
    welcomeToPrefix: "આપનું સ્વાગત છે —",
    ecoTag: "TileSphere · સિરેમિક ઇકોસિસ્ટમ",
    heroTitle: "સાઇન ઇન.",
    heroTitleAccent: "એક જ ઇકોસિસ્ટમ.",
    heroSub: "કિલ્નથી ગ્રાહક સુધી — દરેક ભૂમિકા, દરેક વર્કફ્લો, દરેક ભાગીદાર એક જ મલ્ટી-ટેનન્ટ વર્કસ્પેસમાં.",
    stat1Value: "10+", stat1Label: "બિલ્ટ-ઇન ભૂમિકાઓ",
    stat2Value: "100%", stat2Label: "મલ્ટી-ટેનન્ટ",
    stat3Value: "રિયલ", stat3Label: "Stripe ચૂકવણી",
    emailLabel: "કાર્યકારી ઈમેલ", passwordLabel: "પાસવર્ડ",
    emailPlaceholder: "you@company.com",
    forgotPassword: "પાસવર્ડ ભૂલી ગયા?",
    signInBtn: "સાઇન ઇન", forgotPassword: "પાસવર્ડ ભૂલી ગયા?", signingIn: "સાઇન ઇન થઈ રહ્યું છે…",
    backHome: "હોમ પર પાછા જાઓ",
    signInFailed: "સાઇન ઇન નિષ્ફળ", welcomeBack: "પુનઃ સ્વાગત છે",
    twoFactorRequired: "ટૂ-ફેક્ટર જરૂરી",
    codeFor: "કોડ માટે",
    authCodeLabel: "ઓથેન્ટિકેટર કોડ",
    verifyAndSignIn: "ચકાસો અને સાઇન ઇન કરો",
    verifying: "ચકાસી રહ્યું છે…",
    cancelUseAnother: "રદ કરો અને બીજા એકાઉન્ટનો ઉપયોગ કરો",
    twoFactorNeeded: "ટૂ-ફેક્ટર કોડ જરૂરી",
    demoTitle: "ડેમો · ઓટોફિલ કરવા ક્લિક કરો",
    demoPasswordLabel: "પાસવર્ડ",
  },
  dispatch: {
    title: "ડિસ્પેચ",
    subtitle: "આપોઆપ ઇન્વોઇસ, ઈ-વે બિલ અને ચલણ સાથે માલ મોકલવો.",
    newDispatch: "નવું ડિસ્પેચ", customer: "ગ્રાહક", vehicleNo: "વાહન નં.",
    driver: "ડ્રાઇવર", salesOrder: "સેલ્સ ઓર્ડર",
    createBtn: "ડિસ્પેચ બનાવો અને દસ્તાવેજો જનરેટ કરો",
    invoicePdf: "ઇન્વોઇસ PDF", ewayPdf: "ઈ-વે બિલ PDF", challanPdf: "ચલણ PDF",
  },
  po: {
    title: "પર્ચેઝ ઓર્ડર", newPO: "નવો PO", vendor: "વિક્રેતા", lines: "લાઇન આઇટમ",
    total: "કુલ", submitForApproval: "મંજૂરી માટે સબમિટ કરો", downloadPdf: "PO PDF ડાઉનલોડ",
  },
  finance: {
    receivables: "પ્રાપ્ત", payables: "ચૂકવવાપાત્ર", invoices: "ઇન્વોઇસ",
    aging: "એજિંગ", credits: "ક્રેડિટ નોટ", debits: "ડેબિટ નોટ",
    payments: "ચૂકવણી", ledger: "ખાતાવહી", outstanding: "બાકી", dueDate: "નિયત તારીખ",
  },
  qc: {
    incoming: "ઇનકમિંગ QA", manufacturing: "ઉત્પાદન QA", returns: "ગ્રાહક રિટર્ન QA",
    newInspection: "નવું નિરીક્ષણ", picker: "આઇટમ નામ / PO નંબર / GRN નંબર",
  },
  toasts: {
    saved: "સાચવ્યું", created: "બનાવ્યું", updated: "અપડેટ કર્યું", deleted: "કાઢી નાખ્યું",
    failed: "નિષ્ફળ", submitted: "સબમિટ થયું", approved: "મંજૂર", rejected: "નકારેલ",
  },
  overview: {
    orgFallback: "સંસ્થા",
    welcome: "{{name}}, આપનું સ્વાગત છે.",
    subtitle: "વેચાણ, ડિસ્પેચ, ચૂકવણી, ખરીદી — બધું એક નજરમાં.",
    monthSuffix: "વેચાણ અને રોકડ",
    purchasePulse: "ખરીદી પલ્સ",
    liabilities: "જવાબદારીઓ",
    kpi: {
      dispatchSqm: "ડિસ્પેચ · SQM",
      dispatchSqmSub: "{{count}} ડિસ્પેચ",
      dispatchValue: "ડિસ્પેચ · મૂલ્ય",
      dispatchValueSub: "આ મહિને ઇન્વોઇસ",
      paymentsReceived: "પ્રાપ્ત ચૂકવણી",
      paymentsSub: "{{count}} ચૂકવણી",
      arOutstanding: "બાકી · A/R",
      arSub: "ગ્રાહકો પાસેથી બાકી",
      openPos: "ખુલ્લા PO",
      openPosSub: "{{count}} કુલ",
      openPoValue: "ખુલ્લા PO મૂલ્ય",
      openPoValueSub: "ચાલુ કમિટમેન્ટ",
      pendingPrs: "બાકી PRs",
      pendingPrsSub: "{{count}} કુલ",
      grnsFiled: "GRN દાખલ",
      grnsSub: "કુલ મળેલ",
      apOutstanding: "બાકી · A/P",
      apSub: "વિક્રેતાઓની ચૂકવણી બાકી",
      netWc: "નેટ વર્કિંગ કેપિટલ",
      netWcSub: "A/R − A/P",
    },
  },
  dashboards: {
    sales: {
      welcome: "નમસ્તે {{name}}",
      subtitleManager: "પાઇપલાઇન આરોગ્ય, ટીમ પ્રદર્શન અને આવક એક નજરમાં.",
      subtitleExec: "તમારા લીડ્સ, ક્વોટેશન, ઓર્ડર અને આજની મુલાકાતો.",
    },
    dealer: {
      welcome: "સ્વાગત છે, {{name}}",
      subtitle: "તમારા ઓર્ડર, ચૂકવણી, લોયલ્ટી અને ઓફર — બધું એક જગ્યાએ.",
    },
    distributor: {
      welcome: "નમસ્તે {{name}}",
      subtitle: "પ્રાદેશિક ઇન્વેન્ટરી, ડીલર ઓર્ડર, લોજિસ્ટિક્સ અને કલેક્શન — બધું એક જ વ્યુમાં.",
    },
    vendor: {
      welcome: "પુનઃ સ્વાગત છે, {{name}}.",
      subtitle: "તમારા વર્કસ્પેસનું સ્નેપશોટ.",
    },
    admin: {
      welcome: "પ્લેટફોર્મ કંટ્રોલ રૂમ",
      subtitle: "TileSphere પર દરેક સંસ્થા, વપરાશકર્તા, વ્યવહાર અને એકીકરણનું રીયલ-ટાઇમ વ્યુ.",
    },
    warehouse: {
      welcome: "વેરહાઉસ કંટ્રોલ ટાવર",
      subtitle: "GRN, પિકિંગ, પેકિંગ, ડિસ્પેચ અને સાઇકલ કાઉન્ટ — બધું એક વ્યુમાં.",
    },
    buyer: {
      welcome: "સ્વાગત છે, {{name}}",
      subtitle: "સિરેમિક માર્કેટપ્લેસ બ્રાઉઝ કરો અને તમારા RFQ મેનેજ કરો.",
    },
  },
};

const hi = {
  common: {
    back: "पीछे", save: "सहेजें", cancel: "रद्द करें", create: "बनाएं", update: "अपडेट",
    delete: "हटाएं", edit: "एडिट", submit: "सबमिट", approve: "मंजूर", reject: "अस्वीकार",
    close: "बंद करें", search: "खोजें", filter: "फ़िल्टर", loading: "लोड हो रहा है…",
    yes: "हाँ", no: "नहीं", active: "सक्रिय", inactive: "निष्क्रिय", date: "तारीख़",
    total: "कुल", status: "स्थिति", action: "क्रिया", actions: "क्रियाएँ", number: "नंबर",
    name: "नाम", email: "ईमेल", phone: "फ़ोन", address: "पता", quantity: "मात्रा",
    price: "क़ीमत", amount: "राशि", unit: "इकाई", hsn: "HSN", notes: "नोट्स",
    description: "विवरण", dashboard: "डैशबोर्ड", overview: "ओवरव्यू", settings: "सेटिंग्स",
    logout: "साइन आउट", signIn: "साइन इन", signOut: "साइन आउट", changePassword: "पासवर्ड बदलें", welcome: "स्वागत है",
    download: "डाउनलोड", upload: "अपलोड", print: "प्रिंट", share: "शेयर करें",
    shareEmail: "ईमेल से शेयर करें", shareWhatsApp: "WhatsApp से शेयर करें",
    copyLink: "लिंक कॉपी करें", linkCopied: "लिंक कॉपी हो गया",
    invoice: "इनवॉइस", ewayBill: "ई-वे बिल", challan: "चालान",
    purchaseOrder: "पर्चेज़ ऑर्डर", grn: "GRN", customer: "ग्राहक", vendor: "वेंडर", product: "उत्पाद",
    getStarted: "शुरू करें", register: "रजिस्टर करें", explore: "एक्सप्लोर", exploreMore: "और देखें", learnMore: "और जानें",
    language: "भाषा", twoFactor: "टू-फैक्टर ऑथ", auditLog: "ऑडिट लॉग", plan: "प्लान",
    daysLeft: "{{days}} दिन बाकी", role: "भूमिका",
  },
  language: { label: "भाषा", en: "English", gu: "ગુજરાતી", hi: "हिन्दी" },
  nav: {
    org: {
      overview: "ओवरव्यू", purchase: "ख़रीद", vendors: "वेंडर", customers: "ग्राहक",
      stock: "स्टॉक और वेयरहाउस", qc: "गुणवत्ता नियंत्रण", production: "उत्पादन",
      finance: "वित्त", dispatch: "डिस्पैच", approvals: "स्वीकृति इनबॉक्स",
      users: "उपयोगकर्ता और लॉगिन", roles: "भूमिकाएँ और अनुमतियाँ",
      companyProfile: "कंपनी प्रोफ़ाइल", subscription: "सब्सक्रिप्शन",
      auditCa: "CA ऑडिट", security: "सुरक्षा नियंत्रण",
      sessions: "सक्रिय सत्र", loginAudit: "लॉगिन ऑडिट", integrations: "एकीकरण",
      bulkUpload: "बल्क अपलोड अनुमतियाँ",
    },
    admin: {
      dashboard: "डैशबोर्ड", organizations: "संगठन", subscriptions: "सब्सक्रिप्शन",
      vendors: "वेंडर", distributors: "वितरक",
      platformUsers: "प्लेटफ़ॉर्म उपयोगकर्ता", rolesPermissions: "भूमिकाएँ और अनुमतियाँ",
      marketplace: "मार्केटप्लेस", productMasters: "प्रोडक्ट मास्टर", payments: "भुगतान",
      supportCenter: "सपोर्ट सेंटर", monitoring: "मॉनिटरिंग", security: "सुरक्षा",
      notifications: "सूचनाएँ", reports: "रिपोर्ट्स", cms: "CMS", integrations: "एकीकरण",
      globalSettings: "ग्लोबल सेटिंग्स", platformPlans: "प्लेटफ़ॉर्म · प्लान",
      freeTrials: "प्लेटफ़ॉर्म · मुफ़्त ट्रायल", platformRoles: "प्लेटफ़ॉर्म · भूमिका इंजन",
      platformHierarchy: "प्लेटफ़ॉर्म · श्रेणी", platformPricing: "प्लेटफ़ॉर्म · मूल्य",
      platformLeads: "प्लेटफ़ॉर्म · लीड्स", platformSubs: "प्लेटफ़ॉर्म · सब्सक्रिप्शन",
      auditLogs: "ऑडिट लॉग",
    },
    groups: {
      admin: "एडमिन · अनुशासन", settings: "सेटिंग्स", platformConsole: "प्लेटफ़ॉर्म कंसोल",
      organization: "संगठन", buyer: "खरीदार", vendor: "वेंडर", sales: "बिक्री",
      dealer: "डीलर", distributor: "वितरक", warehouse: "वेयरहाउस",
    },
  },
  landing: {
    navPlatform: "प्लेटफ़ॉर्म", navHierarchy: "श्रेणी", navPortals: "पोर्टल्स",
    heroTag: "TileSphere · सिरेमिक इकोसिस्टम",
    heroTitleLine1: "एक इकोसिस्टम.",
    heroTitleLine2: "हर भूमिका जुड़ी हुई.",
    heroSub: "किल्न से ग्राहक तक — संगठन → वितरक → वेंडर → डीलर → ग्राहक. सब एक ही कॉन्फ़िगरेबल, मल्टी-टेनेंट प्लेटफ़ॉर्म पर.",
    platformKicker: "प्लेटफ़ॉर्म ओवरव्यू",
    platformTitle1: "आपके सिरेमिक बिज़नेस का हर वर्कफ्लो",
    platformTitle2: "— एक ही इकोसिस्टम में.",
    platformSub: "किल्न से ग्राहक तक, TileSphere हर विभाग, पार्टनर और पैलेट को सत्य के एक स्रोत से जोड़ता है.",
    configurable: "कॉन्फ़िगरेबल · मल्टी-टेनेंट",
    hierarchyKicker: "भूमिका श्रेणी",
    hierarchyTitle1: "इकोसिस्टम की हर",
    hierarchyTitle2: "भूमिका देखें.",
    hierarchySub: "किसी भी भूमिका पर क्लिक करके उसकी उप-भूमिकाएँ खोलें. खोजें, फ़िल्टर करें — देखें कि आपकी टीम TileSphere पर कैसे मैप होगी.",
    portalsKicker: "अपना पोर्टल चुनें",
    portalsTitle1: "तीन पोर्टल.",
    portalsTitle2: "एक इकोसिस्टम.",
    portalsSub: "हर पोर्टल एक ख़ास मक़सद के लिए बना है — पर वे एक जैसा डेटा, एक जैसा ब्रांड और एक जैसा ऑडिट लॉग साझा करते हैं.",
    explorePortal: "पोर्टल एक्सप्लोर करें",
    footerRights: "© 2026 TileSphere · Projexino द्वारा बनी इकोसिस्टम",
    core: "कोर",
    modules: {
      organization: "संगठन", distributor: "वितरक", vendor: "वेंडर", dealer: "डीलर",
      customer: "ग्राहक", manufacturing: "उत्पादन", inventory: "इन्वेंटरी",
      warehouse: "वेयरहाउस", sales: "बिक्री", crm: "CRM", marketplace: "मार्केटप्लेस",
      finance: "वित्त", production: "प्रोडक्शन", analytics: "एनालिटिक्स",
      iotReady: "IoT तैयार", cloud: "क्लाउड",
    },
    portals: {
      orgTitle: "संगठन पोर्टल",
      orgTagline: "सिरेमिक निर्माताओं के लिए फुल-स्टैक ERP",
      orgF1: "उत्पादन और किल्न",
      orgF2: "बिक्री · CRM · मार्केटप्लेस",
      orgF3: "वेयरहाउस · डिस्पैच",
      orgF4: "वित्त · अनुपालन",
      distTitle: "वितरक पोर्टल",
      distTagline: "मल्टी-डीलर क्षेत्रीय संचालन और BI",
      distF1: "डीलर ऑनबोर्डिंग",
      distF2: "मूल्य और क्रेडिट सीमा",
      distF3: "लॉजिस्टिक्स और ई-वे बिल",
      distF4: "क्षेत्रीय BI डैशबोर्ड",
      vendorTitle: "वेंडर पोर्टल",
      vendorTagline: "B2B मार्केटप्लेस + टेंडर जवाब",
      vendorF1: "कैटलॉग मैनेजमेंट",
      vendorF2: "RFQ और टेंडर",
      vendorF3: "ऑर्डर पूर्ति",
      vendorF4: "भुगतान और बिलिंग",
    },
  },
  auth: {
    signInTitle: "साइन इन करें",
    signInSubTenant: "{{tenant}} पोर्टल पर",
    signInSubDefault: "अपने TileSphere क्रेडेंशियल्स का उपयोग करें.",
    welcomeTo: "{{tenant}} में आपका स्वागत है.",
    welcomeToPrefix: "स्वागत है —",
    ecoTag: "TileSphere · सिरेमिक इकोसिस्टम",
    heroTitle: "साइन इन.",
    heroTitleAccent: "एक इकोसिस्टम.",
    heroSub: "किल्न से ग्राहक तक — हर भूमिका, हर वर्कफ्लो, हर पार्टनर एक ही मल्टी-टेनेंट वर्कस्पेस में.",
    stat1Value: "10+", stat1Label: "बिल्ट-इन भूमिकाएँ",
    stat2Value: "100%", stat2Label: "मल्टी-टेनेंट",
    stat3Value: "रियल", stat3Label: "Stripe भुगतान",
    emailLabel: "कार्य ईमेल", passwordLabel: "पासवर्ड",
    emailPlaceholder: "you@company.com",
    forgotPassword: "पासवर्ड भूल गए?",
    signInBtn: "साइन इन", forgotPassword: "पासवर्ड भूल गए?", signingIn: "साइन इन हो रहा है…",
    backHome: "होम पर वापस जाएँ",
    signInFailed: "साइन इन विफल", welcomeBack: "पुनः स्वागत है",
    twoFactorRequired: "टू-फैक्टर आवश्यक",
    codeFor: "इसके लिए कोड",
    authCodeLabel: "ऑथेंटिकेटर कोड",
    verifyAndSignIn: "सत्यापित करें और साइन इन",
    verifying: "सत्यापित हो रहा है…",
    cancelUseAnother: "रद्द करें और दूसरा अकाउंट उपयोग करें",
    twoFactorNeeded: "टू-फैक्टर कोड आवश्यक",
    demoTitle: "डेमो · ऑटोफिल के लिए क्लिक करें",
    demoPasswordLabel: "पासवर्ड",
  },
  dispatch: {
    title: "डिस्पैच",
    subtitle: "स्वचालित इनवॉइस, ई-वे बिल और चालान के साथ आउटबाउंड सामान.",
    newDispatch: "नया डिस्पैच", customer: "ग्राहक", vehicleNo: "वाहन नंबर",
    driver: "ड्राइवर", salesOrder: "सेल्स ऑर्डर",
    createBtn: "डिस्पैच बनाएं और दस्तावेज़ जनरेट करें",
    invoicePdf: "इनवॉइस PDF", ewayPdf: "ई-वे बिल PDF", challanPdf: "चालान PDF",
  },
  po: {
    title: "पर्चेज़ ऑर्डर", newPO: "नया PO", vendor: "वेंडर", lines: "लाइन आइटम",
    total: "कुल", submitForApproval: "अनुमोदन हेतु सबमिट", downloadPdf: "PO PDF डाउनलोड",
  },
  finance: {
    receivables: "प्राप्य", payables: "देय", invoices: "इनवॉइस", aging: "एजिंग",
    credits: "क्रेडिट नोट", debits: "डेबिट नोट", payments: "भुगतान", ledger: "बहीखाता",
    outstanding: "बकाया", dueDate: "देय तिथि",
  },
  qc: {
    incoming: "इनकमिंग QA", manufacturing: "मैन्युफ़ैक्चरिंग QA", returns: "ग्राहक रिटर्न QA",
    newInspection: "नया निरीक्षण", picker: "आइटम नाम / PO नंबर / GRN नंबर",
  },
  toasts: {
    saved: "सहेजा गया", created: "बनाया गया", updated: "अपडेट किया गया", deleted: "हटाया गया",
    failed: "विफल", submitted: "सबमिट किया", approved: "स्वीकृत", rejected: "अस्वीकृत",
  },
  overview: {
    orgFallback: "संगठन",
    welcome: "{{name}}, आपका स्वागत है.",
    subtitle: "बिक्री, डिस्पैच, भुगतान, ख़रीद — सब एक नज़र में.",
    monthSuffix: "बिक्री और नक़दी",
    purchasePulse: "ख़रीद पल्स",
    liabilities: "दायित्व",
    kpi: {
      dispatchSqm: "डिस्पैच · SQM",
      dispatchSqmSub: "{{count}} डिस्पैच",
      dispatchValue: "डिस्पैच · मूल्य",
      dispatchValueSub: "इस माह इनवॉइस",
      paymentsReceived: "प्राप्त भुगतान",
      paymentsSub: "{{count}} भुगतान",
      arOutstanding: "बकाया · A/R",
      arSub: "ग्राहकों से बाक़ी",
      openPos: "खुले PO",
      openPosSub: "{{count}} कुल",
      openPoValue: "खुले PO मूल्य",
      openPoValueSub: "चालू कमिटमेंट",
      pendingPrs: "बकाया PRs",
      pendingPrsSub: "{{count}} कुल",
      grnsFiled: "GRN दाखिल",
      grnsSub: "कुल प्राप्तियाँ",
      apOutstanding: "बकाया · A/P",
      apSub: "वेंडरों को भुगतान बाकी",
      netWc: "नेट वर्किंग कैपिटल",
      netWcSub: "A/R − A/P",
    },
  },
  dashboards: {
    sales: {
      welcome: "नमस्ते {{name}}",
      subtitleManager: "पाइपलाइन हेल्थ, टीम प्रदर्शन और राजस्व एक नज़र में.",
      subtitleExec: "आज के आपके लीड्स, कोटेशन, ऑर्डर और विज़िट्स.",
    },
    dealer: {
      welcome: "स्वागत है, {{name}}",
      subtitle: "आपके ऑर्डर, भुगतान, लॉयल्टी और ऑफ़र — सब एक जगह.",
    },
    distributor: {
      welcome: "नमस्ते {{name}}",
      subtitle: "क्षेत्रीय इन्वेंटरी, डीलर ऑर्डर, लॉजिस्टिक्स और कलेक्शन — सब एक व्यू में.",
    },
    vendor: {
      welcome: "पुनः स्वागत है, {{name}}.",
      subtitle: "आपके वर्कस्पेस का स्नैपशॉट.",
    },
    admin: {
      welcome: "प्लेटफ़ॉर्म कंट्रोल रूम",
      subtitle: "TileSphere पर हर संगठन, उपयोगकर्ता, लेनदेन और एकीकरण का रीयल-टाइम व्यू.",
    },
    warehouse: {
      welcome: "वेयरहाउस कंट्रोल टावर",
      subtitle: "GRN, पिकिंग, पैकिंग, डिस्पैच और साइकल काउंट — सब एक व्यू में.",
    },
    buyer: {
      welcome: "स्वागत है, {{name}}",
      subtitle: "सिरेमिक मार्केटप्लेस ब्राउज़ करें और अपने RFQs मैनेज करें.",
    },
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: { en: { translation: en }, gu: { translation: gu }, hi: { translation: hi } },
    fallbackLng: "en",
    supportedLngs: ["en", "gu", "hi"],
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator"],
      caches: ["localStorage"],
      lookupLocalStorage: "tilesphere_lang",
    },
    returnEmptyString: false,
    react: { useSuspense: false },
  });

// Keep <html lang="…"> attribute in sync with active language for a11y & PDFs
if (typeof document !== "undefined") {
  const setLang = (lng) => { document.documentElement.setAttribute("lang", lng || "en"); };
  setLang(i18n.language);
  i18n.on("languageChanged", setLang);
}

export default i18n;
